/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Calls Detail component for final step
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.4.0
 */

import { Fragment, useCallback } from 'react';

import debounce from 'lodash/debounce';
import moment from 'moment';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EmailInput } from '@components/common/EmailInput/EmailInput';
import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';
import {
  makeSelectCallsDetailTranslation,
  makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import type ICommonTranslation from '@modules/internationalization/types/common';
import type { ICallsDetailTranslation } from '@modules/internationalization/types/wizards/callsDetail';
import { WizardReportHeaderStyled } from '@modules/wizard/components/WizardReport/styled/WizardReportStyled';
import WizardReport from '@modules/wizard/components/WizardReport/WizardReport';
import { EWizardReportParam } from '@modules/wizard/components/WizardReport/WizardReportConstants';
import { WizardReportParam } from '@modules/wizard/components/WizardReport/WizardReportParam';
import { makeSelectWizardReportParam } from '@modules/wizard/components/WizardReport/wizardReportSelectors';
import type { IWizardReportParam } from '@modules/wizard/components/WizardReport/wizardReportTypes';
import type { IWizardSummaryParam } from '@modules/wizard/components/WizardSummary/WizardSummary';
import { WizardSummary } from '@modules/wizard/components/WizardSummary/WizardSummary';
import {
  wizardReportSetParamAction,
  wizardReportStorageSaveAction,
} from '@modules/wizard/wizardActions';
import type { IWizardReportSetParamPayload } from '@modules/wizard/wizardTypes';
import type { IRootReducer } from '@reducers/rootReducer';
import { formatBalance } from '@utils/formatText';

import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { callsDetailValidateStart } from '../callsDetailActions';
import { CALLS_DETAIL_PRICE } from '../callsDetailConstants';
import { makeSelectCallsDetailsSummaryParams } from '../callsDetailSelectors';
import type { ICallsDetailSummaryParams } from '../callsDetailTypes';
import { WizardCallsDetailFinalStepContainerStyled } from './styled/WizardCallsDetailsStyled';

export type TWizardCallsDetailStepFinalProps = IConnectedProps & IDispatchProps;

const WizardCallsDetailStepFinal = (
  props: TWizardCallsDetailStepFinalProps,
) => {
  const {
    callsDetailTranslation,
    commonTranslation,
    reportDestEmail,
    // actions
    setNotificationParam,
    validateStart,
  } = props;

  const callsDetailSummaryParams: IWizardSummaryParam[] = [
    ...(!props.summaryParams.basketCount
      ? []
      : [
          {
            title: props.callsDetailTranslation.basketCountLabel,
            values: [`${props.summaryParams.basketCount}`],
          },
        ]),
    {
      title: props.callsDetailTranslation.periodLabel,
      values: [
        `${moment(props.summaryParams.dateFrom).format(EFormatData.FullData)}
             - ${moment(props.summaryParams.dateTo).format(
               EFormatData.FullData,
             )}`,
      ],
    },
    {
      title: props.callsDetailTranslation.priceLabel,
      values: [
        `${formatBalance(CALLS_DETAIL_PRICE)} ${
          props.commonTranslation.currency
        }`,
      ],
    },
    {
      values: [props.callsDetailTranslation.confirmText],
    },
  ];

  const delayedHandleNotificationChange = debounce(() => {
    props.storageSave();
  }, DEBOUNCE_DEFAULT_DELAY);

  const handleChange = useCallback(
    (payload: IWizardReportSetParamPayload) => {
      setNotificationParam(payload);
      validateStart();
      delayedHandleNotificationChange();
    },
    [delayedHandleNotificationChange, setNotificationParam, validateStart],
  );

  return (
    <WizardCallsDetailFinalStepContainerStyled>
      <WizardReport
        notificationValidate={validateStart}
        onChangeParam={delayedHandleNotificationChange}
      >
        <Fragment>
          <WizardReportHeaderStyled>
            {callsDetailTranslation.sendDocumentTitle}
          </WizardReportHeaderStyled>

          <WizardReportParam
            isEnabled={reportDestEmail.isEnabled}
            label={callsDetailTranslation.reports.destEmailForLink}
            setEnabled={(isEnabled: boolean) =>
              handleChange({
                paramType: EWizardReportParam.EMAIL_DEST,
                isEnabled,
              })
            }
          >
            <EmailInput
              emailInputValue={reportDestEmail.value}
              isValid={reportDestEmail.isValid}
              translation={commonTranslation}
              onEmailInputChange={(value: string) =>
                handleChange({
                  paramType: EWizardReportParam.EMAIL_DEST,
                  value,
                })
              }
              onEmailInputValidate={(isValid: boolean) =>
                handleChange({
                  paramType: EWizardReportParam.EMAIL_DEST,
                  isValid,
                })
              }
            />
          </WizardReportParam>
        </Fragment>
      </WizardReport>
      <div>
        <WizardSummary params={callsDetailSummaryParams} />
      </div>
    </WizardCallsDetailFinalStepContainerStyled>
  );
};

export interface IConnectedProps {
  callsDetailTranslation: ICallsDetailTranslation;
  commonTranslation: ICommonTranslation;
  reportDestEmail: IWizardReportParam;
  summaryParams: ICallsDetailSummaryParams;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>(
  {
    callsDetailTranslation: makeSelectCallsDetailTranslation(),
    commonTranslation: makeSelectTranslationCommon(),
    reportDestEmail: makeSelectWizardReportParam(EWizardReportParam.EMAIL_DEST),
    summaryParams: makeSelectCallsDetailsSummaryParams(),
  },
);

export interface IDispatchProps {
  setNotificationParam: (payload: IWizardReportSetParamPayload) => void;
  storageSave: () => void;
  validateStart: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  setNotificationParam: (payload) =>
    dispatch(wizardReportSetParamAction(payload)),
  storageSave: () => dispatch(wizardReportStorageSaveAction()),
  validateStart: () => dispatch(callsDetailValidateStart()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(WizardCallsDetailStepFinal);
